import { useState, useEffect, useRef } from "react";

// global context for state management
import { useSampleContext } from "../../contexts";
import { useReachbackContext } from "../../contexts/ReachbackContext";

// components
import { SequenceE } from "../ui/Sequence";
import CustomModalB from "../ui/ModalB";

// queries
import { useGetMeasurementBulk, useValidateSample } from "../../api";

import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

// lodash to help with object comparisons
const some = require("lodash.some");

const WizardBulk = ({ step, steps, changeStep, pageID }) => {
  const [scanResults, setScanResults] = useState([]);
  const [selected, setSelected] = useState([]);
  const [counter, setCounter] = useState(1);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unverifiedScan, setUnverifiedScan] = useState();
  const [showModal, setShowModal] = useState(false);
  // Only process a bulk scan result that was requested from this session
  const awaitingScanRef = useRef(false);
  // Only append validated scan once per request
  const awaitingValidateRef = useRef(false);

  const { initWasatchBulk, device } = useSampleContext();
  const { updateSubmittedErrorsB, addReachbackReason, removeReachbackReason } =
    useReachbackContext();

  const {
    data: bulkScan,
    status: bulkScanStatus,
    isFetching: isFetchingbulkScan,
    refetch: refetchbulkScan,
  } = useGetMeasurementBulk(
    device?.deviceId,
    "bulk-burst",
    connectorAppErrorLogger
  );

  const {
    data: validateResponse,
    status: validateStatus,
    isFetching: isFetchingValidate,
    refetch: refetchValidate,
  } = useValidateSample(unverifiedScan, sampleCheckErrorLogger);

  // Helper function to get the two newest scans
  const getTwoNewestScans = (results) => {
    return results.sort((a, b) => b.scanId - a.scanId).slice(0, 2);
  };

  // Initialize the scan results state with the two newest scans
  const [twoNewestScans, setTwoNewestScans] = useState(() =>
    getTwoNewestScans(scanResults)
  );

  // Update the two newest scans whenever scanResults change
  useEffect(() => {
    setTwoNewestScans(getTwoNewestScans(scanResults));
  }, [scanResults]);

  // Reset selected scan if it no longer exists in the two newest scans
  useEffect(() => {
    if (!doesScanExist(selected.scanId)) {
      setSelected({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [twoNewestScans]);

  // Check if the scan exists in the two newest scans
  const doesScanExist = (scanId) => {
    return some(twoNewestScans, (scan) => scan.scanId === scanId);
  };

  function connectorAppErrorLogger(err) {
    console.log("bulk scan error logged.");
    setLoading(false);
    setError(true);
    // Only reset scan ref - validation hasn't started yet if scan failed
    awaitingScanRef.current = false;
    console.error(err);
  }

  function sampleCheckErrorLogger(err) {
    console.log("Error logged.");
    setLoading(false);
    setError(true);
    // Reset both scan and validate refs to handle defensive reset
    awaitingScanRef.current = false;
    awaitingValidateRef.current = false;
    console.error(err);
  }

  const handleScan = () => {
    console.log("Performing bulk scan");
    setLoading(true);
    setError(false);
    // Mark that the next successful query result should be processed
    awaitingScanRef.current = true;
    refetchbulkScan();
    setShowModal(false);
  };

  const handleModal = () => {
    // After completing the scan, show the modal
    setShowModal(true);
  };

  // Once the bulk scan is retrieved, prepare it for validation
  useEffect(() => {
    // Only handle a bulk result if it was triggered by the current button press
    if (
      awaitingScanRef.current &&
      !isFetchingbulkScan &&
      step === pageID &&
      bulkScanStatus === "success"
    ) {
      setUnverifiedScan({ ...bulkScan, scanId: counter });
      setCounter((prev) => prev + 1);
      // Mark that we're awaiting a single validate result
      awaitingValidateRef.current = true;
      // Consume the fetch flag so cached data doesn't reprocess
      awaitingScanRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkScan, isFetchingbulkScan, bulkScanStatus, step, pageID]);

  // Only attempt to validate scan once react has set the 'unverifiedScan' variable
  useEffect(() => {
    if (unverifiedScan) {
      refetchValidate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unverifiedScan]);

  // Check the scan validate query is done
  useEffect(() => {
    if (!isFetchingValidate && step === pageID) {
      if (validateStatus === "success" && awaitingValidateRef.current) {
        let verifiedScan = { ...unverifiedScan };

        verifiedScan.verified =
          validateResponse?.data?.errorCheck?.errorFlag === 0;

        // Always add errorCheck properties
        verifiedScan.errorCheck = {
          // errorFlag: subScan?.errorCheck?.errorFlag,
          // errorMessage: subScan?.errorCheck?.errorMessage,
          // errorType: subScan?.errorCheck?.errorType,
          errorFlag: validateResponse?.data?.errorCheck?.errorFlag,
          modelPrediction: validateResponse?.data?.errorCheck?.modelPrediction,
          reconstructionError:
            validateResponse?.data?.errorCheck?.reconstructionError_intensity,
          signalQuality: validateResponse?.data?.errorCheck?.signalQuality,
        };
        console.log(verifiedScan);

        setScanResults((prev) => [...prev, verifiedScan]);
        setLoading(false);
        setError(false);
        // Clear validation flag after successful processing
        awaitingValidateRef.current = false;
      } else if (validateStatus === "error") {
        console.error("Could not validate scan.");
        // Clear validation flag - scan succeeded but validation failed
        awaitingValidateRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validateResponse, isFetchingValidate]);

  if (step !== pageID) {
    return null;
  }

  // // handler function for the "Continue" button click event
  // function handleContinue() {
  //   // update the step to move forward
  //   changeStep("forward");
  // }

  const handleSelectSequence = (sequence) => {
    setSelected(sequence);
  };

  return (
    <div className='shadow card d-flex flex-column flex-grow-1 '>
      <div className='card-body full-height justify-content-between'>
        <div
          className={
            twoNewestScans.length !== 0 && !error
              ? "d-flex justify-content-between flex-wrap"
              : "d-flex flex-column justify-content-between flex-grow-1 "
          }
        >
          <div
            className={
              twoNewestScans.length !== 0 && !error ? "col-12 col-md-6" : "row "
            }
          >
            <h5 className='card-title w-100'>
              <span>Amplifi ID Bulk Scan</span>
              <button
                className='btn ms-1 p-1'
                aria-label='Info'
                onClick={handleModal}
              >
                <i
                  className='bi bi-info-circle-fill'
                  style={{ color: "#1ca3f0" }}
                ></i>
              </button>
            </h5>

            <h6 className='card-subtitle mb-2 mb-md-4 text-muted w-100'>
              If Bulk Scan is not applicable, press "Skip Bulk Scan".
            </h6>
          </div>

          <div
            className={
              twoNewestScans.length !== 0 && !error
                ? "col-12 col-md-6 d-flex justify-content-start justify-content-md-end align-items-center"
                : "row pt-4 d-flex flex-column flex-grow-1 justify-content-center align-items-center"
            }
          >
            {!loading && (
              <button
                className='btn btn-link float-end order-1'
                type='button'
                onClick={() => {
                  initWasatchBulk(null);
                  changeStep("forward");
                  updateSubmittedErrorsB(false);
                }}
              >
                Skip Bulk Scan
              </button>
            )}
            <button
              className='btn btn-success float-end w-50 '
              type='button'
              variant='outline-success'
              onClick={() => handleScan()}
              disabled={loading}
            >
              {loading ? (
                <span>
                  <span
                    className='spinner-border spinner-border-sm'
                    aria-hidden='true'
                  ></span>
                  <span> Scanning...</span>
                </span>
              ) : (
                <span>Conduct Bulk Scan</span>
              )}
            </button>
            <CustomModalB
              showModal={showModal}
              handleClose={() => setShowModal(false)}
            />
          </div>
        </div>

        {/* Plots */}
        {twoNewestScans.length !== 0 && !error && (
          <form
            className={
              "row justify-content-evenly d-flex align-items-stretch flex-wrap flex-grow-1"
            }
          >
            {bulkScanStatus === "success" &&
              twoNewestScans.map((seq) => {
                return (
                  <div
                    className='col-12 col-xl-6 mt-2 spectrum-card-container '
                    key={seq.scanId}
                  >
                    <SequenceE
                      key={seq.scanId}
                      sequence={seq}
                      isSelected={selected.scanId === seq.scanId}
                      valid={seq.verified}
                      // validateResponse={validateResponse}
                      onSelect={handleSelectSequence}
                    />
                  </div>
                );
              })}
          </form>
        )}

        {/* Back and Continue buttons */}

        {/* <div className="row mt-4">{error && <h6>{errMsg}</h6>}</div> */}
        <div className='row mt-4 align-content-center'>
          <span className='col-6 d-flex justify-content-center'>
            <button
              type='button'
              className='btn btn-secondary mx-auto w-75'
              onClick={() => changeStep("back")}
              disabled={loading}
            >
              Back
            </button>
          </span>
          <OverlayTrigger
            overlay={
              Object.keys(selected).length === 0 ? (
                <Tooltip id='tooltip-disabled'>
                  Please conduct and select a scan to continue. Otherwise, press
                  "Skip Bulk Scan".
                </Tooltip>
              ) : (
                <></>
              )
            }
          >
            <span className='col-6 d-flex justify-content-center'>
              <button
                type='button'
                className='btn btn-primary mx-auto w-75'
                onClick={() => {
                  // if (!submittedErrorsB) {
                  const hasError =
                    selected.errorCheck?.modelPrediction !== "Analyte";
                  console.log("hasError? : ", hasError);

                  if (hasError) {
                    updateSubmittedErrorsB(true); // Set submittedErrorsB to true
                    addReachbackReason({
                      bulkErrorCheck: selected.errorCheck.signalQuality,
                    });
                  } else {
                    updateSubmittedErrorsB(false); // Set submittedErrorsB to false
                    removeReachbackReason("bulkErrorCheck");
                  }
                  // }
                  initWasatchBulk(selected);
                  changeStep("forward");
                }}
                disabled={Object.keys(selected).length === 0 || loading}
              >
                {selected.errorCheck?.modelPrediction === "Analyte" ||
                Object.keys(selected).length === 0
                  ? "Continue"
                  : "Continue and Submit for Reachback Service"}
              </button>
            </span>
          </OverlayTrigger>
        </div>
      </div>
    </div>
  );
};

export default WizardBulk;
